import { hashFile } from '@st/util/archive/hash-file'
import type { FileEntry } from '@st/util/archive/file-entry'
import type { UploadSelection } from '@st/util/archive/unpack-upload'
import type { STDocument } from '@st/sdk'
import { Effect, Stream } from 'effect'
import { BrowserResources, type BrowserResourceRef } from '~/effects'
import {
  detectUploadContainer,
  resolveFilesFromUploadContainer
} from '~/features/document-uploads/detect-upload-type'
import { reportHandledError } from '~/features/error-reporting/report-handled-error'

type BulkUploadFileEntry = {
  path: string
  name: string
  size: number
  type: string
}

type BulkUploadDuplicateFileEntry = BulkUploadFileEntry & {
  duplicates: Array<{ name: string }>
}

export type BulkUploadEntries = {
  uniqueFiles: BulkUploadFileEntry[]
  duplicates: BulkUploadDuplicateFileEntry[]
}

export type BulkUploadSource = 'zip' | 'folder'

export type BulkUploadDialogState =
  | { status: 'resolving'; source: BulkUploadSource }
  | {
      status: 'resolved'
      source: BulkUploadSource
      entries: BulkUploadEntries
      files: BrowserResourceRef<FileEntry[]>
    }
  | { status: 'failed'; source: BulkUploadSource }

export type BulkUploadState = {
  dialog: BulkUploadDialogState | undefined
  session: { upload: BrowserResourceRef<UploadSelection>; source: BulkUploadSource } | undefined
}

type ResolvedBulkUploadEntry = BulkUploadFileEntry & {
  hash: string
}

export type BulkUploadMessage =
  | {
      type: 'started'
      upload: BrowserResourceRef<UploadSelection>
      source: BulkUploadSource
    }
  | {
      type: 'resolved'
      entries: ResolvedBulkUploadEntry[]
      files: BrowserResourceRef<FileEntry[]>
    }
  | { type: 'failed' }
  | { type: 'transferred' }
  | { type: 'closed' }

export function initBulkUploadState(): BulkUploadState {
  return {
    dialog: undefined,
    session: undefined
  }
}

export function reduceBulkUpload(args: {
  state: BulkUploadState
  message: BulkUploadMessage
  existingDocuments: STDocument[]
}): BulkUploadState {
  const { state, message, existingDocuments } = args

  switch (message.type) {
    case 'started':
      return {
        dialog: { status: 'resolving', source: message.source },
        session: { upload: message.upload, source: message.source }
      }
    case 'resolved': {
      const session = requireBulkUploadSession(state, message)
      return {
        ...state,
        dialog: {
          status: 'resolved',
          source: session.source,
          entries: classifyBulkUploadEntries(message.entries, existingDocuments),
          files: message.files
        }
      }
    }
    case 'failed': {
      const session = requireBulkUploadSession(state, message)
      return {
        ...state,
        dialog: { status: 'failed', source: session.source },
        session: undefined
      }
    }
    case 'transferred': {
      requireBulkUploadSession(state, message)
      return {
        ...state,
        dialog: undefined,
        session: undefined
      }
    }
    case 'closed':
      return {
        ...state,
        dialog: undefined,
        session: undefined
      }
  }
}

function requireBulkUploadSession(
  state: BulkUploadState,
  message: Extract<BulkUploadMessage, { type: 'resolved' | 'failed' | 'transferred' }>
) {
  if (!state.session) {
    throw new Error(`Unexpected ${message.type} message without an active bulk-upload session`)
  }
  return state.session
}

/**
 * Owns the captured browser selection until the upload module takes its file
 * lease. Closing the dialog removes the state-derived Task, which releases the
 * selection and resolved files together.
 */
export function resolveBulkUpload(uploadId: string) {
  return Stream.unwrap(
    Effect.gen(function* () {
      const browserResources = yield* BrowserResources
      const uploadSelection = yield* browserResources.take<UploadSelection>({ id: uploadId })
      const uploadContainer = yield* detectUploadContainer(uploadSelection)
      const entries = yield* resolveFilesFromUploadContainer(uploadContainer)
      const resolvedEntries = yield* Effect.forEach(
        entries,
        (entry) =>
          hashFile(entry).pipe(
            Effect.map(
              (hash): ResolvedBulkUploadEntry => ({
                path: entry.path,
                name: entry.file.name,
                size: entry.file.size,
                type: entry.file.type,
                hash
              })
            )
          ),
        { concurrency: 10 }
      )
      const files = yield* browserResources.hold(entries)

      return Stream.concat(
        Stream.succeed({
          type: 'resolved',
          entries: resolvedEntries,
          files: files.reference
        } as const),
        Stream.fromEffect(files.taken).pipe(Stream.map(() => ({ type: 'transferred' }) as const))
      )
    })
  ).pipe(
    Stream.catchTags({
      FileReadError: (error) => Stream.fromEffect(failBulkUpload('read-files', error.cause)),
      InvalidArchiveError: (error) => Stream.fromEffect(failBulkUpload('read-archive', error.cause))
    })
  )
}

function classifyBulkUploadEntries(
  entries: ResolvedBulkUploadEntry[],
  existingDocuments: STDocument[]
): BulkUploadEntries {
  const documentsByContent = new Map<string, STDocument[]>()
  for (const document of existingDocuments) {
    if (document.fileSize == null || document.hash == null) continue
    const contentKey = `${document.fileSize}:${document.hash}`
    const documents = documentsByContent.get(contentKey) ?? []
    documents.push(document)
    documentsByContent.set(contentKey, documents)
  }

  const uniqueFiles: BulkUploadEntries['uniqueFiles'] = []
  const duplicates: BulkUploadEntries['duplicates'] = []
  for (const entry of entries) {
    const { hash, ...file } = entry
    const matchingDocuments = documentsByContent.get(`${entry.size}:${hash}`) ?? []
    if (matchingDocuments.length == 0) {
      uniqueFiles.push(file)
    } else {
      duplicates.push({
        ...file,
        duplicates: matchingDocuments.map((document) => ({ name: document.name }))
      })
    }
  }

  return { uniqueFiles, duplicates }
}

function failBulkUpload(operation: string, cause: unknown) {
  return reportHandledError(cause, {
    source: 'st-folder',
    operation: `resolve-bulk-upload-${operation}`
  }).pipe(
    Effect.as({
      type: 'failed'
    } as const)
  )
}
